import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { BRICKS_CATALOGUE, BRICKS_SPECS } from "@/lib/data";

const HERO = "/bricks/rustic-red.jpg";

export default function Bricks() {
  const [lightbox, setLightbox] = useState(null);
  const close = useCallback(() => setLightbox(null), []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  return (
    <div data-testid="bricks-page" className="bg-cream">
      {/* ===== Page heading ===== */}
      <section className="px-4 sm:px-6 md:px-16 lg:px-20 pt-20 sm:pt-22 md:pt-28 pb-12 sm:pb-16">
        <div className="max-w-[1600px] mx-auto grid grid-cols-12 gap-6 sm:gap-8">
          <div className="col-span-12 md:col-span-4">
            <p className="font-mono-label text-terracotta">Cement Bricks</p>
          </div>
          <div className="col-span-12 md:col-span-8">
            <h1 className="font-serif-display text-4xl sm:text-5xl md:text-7xl leading-[1.0]">
              Cladding <em className="italic text-terracotta">bricks</em>,<br/>engineered for architects.
            </h1>
          </div>
        </div>
      </section>

      {/* ===== Intro + hero image ===== */}
      <section className="px-4 sm:px-6 md:px-16 lg:px-20 pb-10 sm:pb-14 md:pb-18">
        <div className="max-w-[1600px] mx-auto grid grid-cols-12 gap-8 sm:gap-10 md:gap-12 lg:items-center">
          <div className="col-span-12 lg:col-span-7 space-y-4 sm:space-y-5 order-2 lg:order-1">
            <p className="font-serif-display text-xl sm:text-2xl md:text-3xl leading-snug">
              A commitment to excellence. Our quality is our identity.
            </p>
            <p className="text-sm sm:text-base text-stone-text leading-relaxed">
              Our cladding bricks are pressed, pigmented, and cured to deliver a consistent architectural face with the depth and warmth of natural masonry. Two finish families — <b className="text-ink">Plain</b> for clean modern surfaces and <b className="text-ink">Rustic</b> for hand-textured warmth — each available in five colours plus custom combinations.
            </p>
            <p className="text-sm sm:text-base text-stone-text leading-relaxed">
              Every brick meets a compressive strength of <b className="text-ink">20.44 N/mm²</b>, water absorption below 10%, and a nil efflorescence rating. Reference any code (e.g. <b className="text-ink">104 · Rustic Burnt Red</b>) when requesting a sample.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-5 order-1 lg:order-2">
            <div className="image-zoom-wrap aspect-[4/3] max-w-md mx-auto lg:max-w-none">
              <img src={HERO} alt="Cement cladding brick" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== Series ===== */}
      {BRICKS_CATALOGUE.map((series, sIdx) => (
        <section
          key={series.id}
          id={series.id}
          data-testid={`brick-series-${series.id}`}
          className={`scroll-mt-20 sm:scroll-mt-24 px-4 sm:px-6 md:px-16 lg:px-20 py-10 sm:py-14 md:py-18 ${
            sIdx % 2 === 0 ? "bg-stone" : "bg-cream"
          }`}
        >
          <div className="max-w-[1600px] mx-auto">
            <div className="text-center mb-8 sm:mb-10 md:mb-14">
              <p className="font-mono-label text-terracotta mb-3">{`0${sIdx + 1}`}</p>
              <h2 className="font-serif-display text-3xl sm:text-4xl md:text-6xl">
                {series.label.split(" ")[0]}{" "}
                <em className="italic text-terracotta">{series.label.split(" ")[1]}</em>
              </h2>
              <div className="w-10 sm:w-12 h-px bg-ink/30 mx-auto mt-5 sm:mt-6" />
              <p className="font-mono-label text-stone-text mt-5 sm:mt-6 text-[0.6rem] sm:text-xs px-4">
                {series.tagline} · {series.dimensions} · {series.packing}
              </p>
            </div>

            <div
              data-testid={`brick-grid-${series.id}`}
              className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6 md:gap-8"
            >
              {series.items.map((item) => (
                <button
                  key={item.code + item.name}
                  data-testid={`brick-tile-${series.id}-${item.code}`}
                  onClick={() => setLightbox({ ...item, series: series.label })}
                  className="group text-left focus:outline-none focus:ring-2 focus:ring-terracotta focus:ring-offset-2 focus:ring-offset-cream"
                  aria-label={`View ${item.name}`}
                >
                  <div className="image-zoom-wrap aspect-[4/3] bg-warm/40 overflow-hidden">
                    <img src={item.image} alt={item.name} loading="lazy" className="w-full h-full object-cover" />
                  </div>
                  <div className="mt-3 sm:mt-4 flex items-baseline justify-between gap-3 border-t border-warm pt-2.5 sm:pt-3">
                    <p className="font-serif-display text-lg sm:text-xl md:text-2xl group-hover:text-terracotta transition-colors leading-tight">
                      {item.name}
                    </p>
                    <p className="font-mono-label text-stone-text text-[0.6rem] sm:text-xs shrink-0">
                      {item.code}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* ===== Technical specs ===== */}
      <section data-testid="bricks-specs" className="bg-ink text-cream py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-16 lg:px-20">
        <div className="max-w-[1600px] mx-auto grid grid-cols-12 gap-8 sm:gap-10">
          <div className="col-span-12 md:col-span-4">
            <p className="font-mono-label mb-3" style={{color: "rgba(243,236,224,0.55)"}}>— Technical Specification</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl leading-[1.05]">
              Numbers that hold up on <em className="italic" style={{color: "#c8ac7b"}}>site</em>.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-8 md:pl-8">
            <ul className="divide-y" style={{ borderTopWidth: 1, borderColor: "rgba(243,236,224,0.15)"}}>
              {BRICKS_SPECS.map(([k, v]) => (
                <li key={k} className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-6 py-5 sm:py-6" style={{borderColor: "rgba(243,236,224,0.15)"}}>
                  <span className="font-mono-label" style={{color: "rgba(243,236,224,0.6)"}}>{k}</span>
                  <span className="font-serif-display text-xl sm:text-2xl md:text-3xl" style={{color: "#c8ac7b"}}>{v}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="px-4 sm:px-6 md:px-16 lg:px-20 py-12 sm:py-16 md:py-20 bg-cream">
        <div className="max-w-[1200px] mx-auto text-center">
          <p className="font-mono-label text-stone-text mb-3 sm:mb-4">Next step</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl md:text-6xl leading-[1.05]">
            Order a physical <em className="italic text-terracotta">sample</em>.
          </h2>
          <p className="mt-5 sm:mt-6 text-sm sm:text-base text-stone-text max-w-xl mx-auto">
            Quote the brick code on the enquiry form, mention the surface area, and a senior from the studio will ship you a physical brick within days.
          </p>
          <Link
            to="/request-quote"
            data-testid="bricks-cta"
            className="inline-flex mt-8 sm:mt-10 font-mono-label px-7 sm:px-8 py-3.5 sm:py-4 bg-ink text-cream hover:bg-terracotta transition-colors"
          >
            Request a Sample →
          </Link>
        </div>
      </section>

      {/* ===== Lightbox ===== */}
      {lightbox && (
        <div
          data-testid="brick-lightbox"
          onClick={close}
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur flex items-center justify-center p-4 sm:p-6 md:p-12 cursor-zoom-out"
        >
          <button
            data-testid="brick-lightbox-close"
            onClick={close}
            aria-label="Close"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 md:top-10 md:right-10 font-mono-label text-cream hover:text-terracotta z-10 px-3 py-2 bg-ink/50 rounded"
          >
            Close ✕
          </button>
          <div className="max-w-[92vw] max-h-[85vh] flex flex-col items-center gap-4 sm:gap-5" onClick={(e)=>e.stopPropagation()}>
            <img src={lightbox.image} alt={lightbox.name} className="max-w-full max-h-[72vh] sm:max-h-[78vh] object-contain shadow-2xl" />
            <div className="flex items-baseline gap-4 sm:gap-6 text-cream">
              <p className="font-serif-display text-2xl sm:text-3xl md:text-4xl">{lightbox.name}</p>
              <p className="font-mono-label text-cream/60">{lightbox.code} · {lightbox.series}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
